import emailjs from "@emailjs/browser";

// EmailJS Configuration
// Sign up at https://www.emailjs.com/ and replace these with your actual values:
// 1. EMAILJS_SERVICE_ID  - from Email Services tab
// 2. EMAILJS_TEMPLATE_ID - from Email Templates tab
// 3. EMAILJS_PUBLIC_KEY   - from Account > API Keys
const EMAILJS_SERVICE_ID = "service_e8x5uvn";
const EMAILJS_TEMPLATE_ID = "template_c6pf6uk";
const EMAILJS_JOINING_TEMPLATE_ID = "template_joining"; // Create this template in EmailJS
const EMAILJS_PUBLIC_KEY = "AL2R7in6J2dcMe5A5";

// The email address that receives enquiry notifications
const NOTIFICATION_EMAIL = "hello@urban59.co.uk";

// App URL for joining instructions
const APP_URL = "https://urban59.co.uk";

/**
 * Sends an email notification when a new enquiry is submitted.
 * Fails silently so it doesn't block the enquiry submission.
 */
export const sendEnquiryNotification = async (formData) => {
	try {
		await emailjs.send(
			EMAILJS_SERVICE_ID,
			EMAILJS_TEMPLATE_ID,
			{
				to_email: NOTIFICATION_EMAIL,
				from_name: `${formData.firstName} ${formData.lastName}`,
				from_email: formData.email,
				phone: formData.phone,
				sessions_per_week: formData.sessionsPerWeek,
				motivation: formData.motivation || "Not provided",
				readiness:
					formData.readiness === "ready"
						? "Yes, ready to take action"
						: "Would like to learn more first",
				call_time: formData.callTime,
				// Location context so notification emails reference the right site.
				location_id: formData.locationId || "sidcup",
				location_name: formData.locationName || "Urban 59 Sidcup",
			},
			EMAILJS_PUBLIC_KEY,
		);
		console.log("Enquiry notification email sent successfully");
	} catch (error) {
		// Log but don't throw — the Firestore write is the important part
		console.error("Failed to send enquiry notification email:", error);
	}
};

/**
 * Sends joining instructions email to a new user.
 * Contains their login details and a link to the app.
 *
 * Uses the shared template (template_joining) which expects:
 *   {{to_name}}        - The user's name
 *   {{to_email}}       - The user's email (also used as the recipient)
 *   {{{body_content}}} - The full HTML body between the logo and footer
 *
 * Returns { success: true } or { success: false, error: ... }
 */
export const sendJoiningInstructions = async ({
	userName,
	userEmail,
	tempPassword,
	trainerName,
}) => {
	// Build the password section HTML based on whether we have a temp password
	const passwordSection = tempPassword
		? `<p style="margin: 0 0 12px 0; line-height: 1.6; color: #333333;">
				Go to <a href="https://urban59.co.uk" target="_blank" style="color: #d15a36; text-decoration: none; font-weight: bold;">urban59.co.uk</a> and log in with your email and this password:
			</p>
			<div style="background-color: #fff8f1; border-radius: 6px; padding: 12px 16px; text-align: center; margin-bottom: 12px;">
				<span style="color: #d15a36; font-size: 20px; font-weight: bold; letter-spacing: 1px;">${tempPassword}</span>
			</div>
			<p style="margin: 0 0 8px 0; line-height: 1.6; color: #333333;">
				You can change it once you're in.
			</p>`
		: `<p style="margin: 0 0 8px 0; line-height: 1.6; color: #333333;">
				Go to <a href="https://urban59.co.uk" target="_blank" style="color: #d15a36; text-decoration: none; font-weight: bold;">urban59.co.uk</a> and log in with your email and existing password. If you've forgotten it, use the forgot password option on the login page or contact us at <a href="mailto:hello@urban59.co.uk" style="color: #d15a36; text-decoration: none;">hello@urban59.co.uk</a>.
			</p>`;

	const bodyContent = `<!-- Welcome -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 24px; margin-bottom: 16px; border-left: 4px solid #d15a36;">
<h1 style="color: #d15a36; font-size: 22px; margin: 0 0 16px 0;">Welcome to Urban 59, ${userName}!</h1>
<p style="margin: 0 0 12px 0; line-height: 1.6; color: #333333;">We're so happy that you've chosen us and are ready to take that positive step in achieving your health and fitness goals. Please find below some key info.</p>
<p style="margin: 0; line-height: 1.6; color: #333333;">If you have any problems, email us at <a href="mailto:hello@urban59.co.uk" style="color: #d15a36; text-decoration: none;">hello@urban59.co.uk</a> or WhatsApp Ami on <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">07465\u200B 224191</span>.</p>
</div>
<!-- Arriving -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Arriving</h2>
<p style="margin: 0 0 8px 0; line-height: 1.6; color: #333333;">Please arrive <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">2\u200B\u20133 minutes</span> early. The front door will be open so just come on straight in.</p>
<p style="margin: 0; line-height: 1.6; color: #555555;"><strong style="color: #333333;">59 Lewis Road, DA14 4NB</strong></p>
</div>
<!-- Parking -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Parking</h2>
<p style="margin: 0; line-height: 1.6; color: #333333;">No parking with wheels on the pavement (tickets are being issued) and please don't block driveways at all, even slightly. Best option is <strong>Luton Rd</strong> (1 min walk) or that end of Lewis Rd.</p>
</div>
<!-- Cancellations -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Cancellations</h2>
<p style="margin: 0 0 10px 0; line-height: 1.6; color: #333333;">Please cancel at least <strong><span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">12\u200B hrs</span></strong> before your session + a <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">4\u200B hr</span> overnight block out otherwise you will lose it from your allowance.</p>
<table style="width: 100%; border-collapse: collapse; margin-bottom: 8px;"><tr><td style="padding: 6px 12px; background-color: #fff8f1; border-radius: 4px 4px 0 0; color: #333333; border-bottom: 1px solid #f0dcc8;"><strong><span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">10\u200Bam</span> sessions</strong> \u2192 cancel by <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">6\u200Bpm</span> the night before</td></tr><tr><td style="padding: 6px 12px; background-color: #fff8f1; border-radius: 0 0 4px 4px; color: #333333;"><strong><span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">1\u200Bpm</span> sessions</strong> \u2192 cancel by <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">10\u200Bpm</span> the night before</td></tr></table>
<p style="margin: 0; line-height: 1.6; color: #888888; font-size: 13px;">(Helps waitlist members get in)</p>
</div>
<!-- Fit For Life -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Fit For Life Sessions</h2>
<p style="margin: 0; line-height: 1.6; color: #333333;">Open to everyone \u2014 just a slower paced, less technical option. Great if you're new, managing an injury, pregnant/postpartum or easing back into training.</p>
</div>
<!-- TeamUp App -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">TeamUp App</h2>
<p style="margin: 0 0 12px 0; line-height: 1.6; color: #333333;">Download the app and log in. It's the best way to book, cancel &amp; manage sessions quickly. You can book all sessions up to <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">14\u200B days</span> in advance. If you see a session you'd like to attend has a waitlist, do pop yourself on the list as often a spot will become available. You'll be notified via email if it does.</p>
<div style="margin-bottom: 8px;"><a href="https://apps.apple.com/gb/app/teamup-members/id1449504627" target="_blank" style="color: #d15a36; text-decoration: none; font-weight: bold;">Download for iOS (App Store)</a></div>
<div><a href="https://play.google.com/store/apps/details?id=com.teamup.members" target="_blank" style="color: #d15a36; text-decoration: none; font-weight: bold;">Download for Android (Google Play)</a></div>
</div>
<!-- Urban 59 Webapp -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px; border: 1px solid #d15a36;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Urban 59 Webapp</h2>
${passwordSection}
<p style="margin: 0; line-height: 1.6; color: #777777; font-size: 13px;">To save the app to your Home Screen: tap the <strong>three dots</strong> \u2192 <strong>Share</strong> \u2192 <strong>Add to Home Screen</strong>. You'll use this app to track your progress throughout your sessions.</p>
</div>
<!-- Timekeeping -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Timekeeping</h2>
<p style="margin: 0; line-height: 1.6; color: #333333;">Please arrive on time so you don't miss your warm up &amp; to avoid disrupting the session. If you arrive late you might not be accepted into the session.</p>
</div>
<!-- WhatsApp -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Community WhatsApp</h2>
<p style="margin: 0; line-height: 1.6; color: #333333;">It's important that you join the Urban 59 Community WhatsApp Group, as all key updates will be shared here. Don't worry, you won't be spammed with lots of messages. The group is set to one way messaging, so only coaches can post. <a href="https://chat.whatsapp.com/EUdEMg59HxM7erj0MV3uSa?mode=gi_t">Click here</a> to join.</p>
</div>
<!-- Respect the Area -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Respect the Area</h2>
<p style="margin: 0; line-height: 1.6; color: #333333;">We're in a residential area, so please keep noise down after <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">8\u200Bpm</span> sessions when chatting outside.</p>
</div>
<!-- Referrals -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 20px; margin-bottom: 16px;">
<h2 style="color: #d15a36; font-size: 17px; margin: 0 0 10px 0;">Referrals</h2>
<p style="margin: 0; line-height: 1.6; color: #333333;">Share your code via the TeamUp app \u2192 your friend gets a <strong>free session</strong>, and if they join, you get <strong>\u00A325 off</strong> your next payment.</p>
</div>`;

	try {
		await emailjs.send(
			EMAILJS_SERVICE_ID,
			EMAILJS_JOINING_TEMPLATE_ID,
			{
				to_name: userName,
				to_email: userEmail,
				body_content: bodyContent,
				email_subject: `Welcome to Urban 59, ${userName}!`,
			},
			EMAILJS_PUBLIC_KEY,
		);
		console.log("Joining instructions email sent to:", userEmail);
		return { success: true };
	} catch (error) {
		console.error("Failed to send joining instructions email:", error);
		return {
			success: false,
			error: error?.text || error?.message || "Unknown error",
		};
	}
};

/**
 * Sends a follow-up email to a contact added via the Quick Add form.
 * Reuses the joining instructions template (template_joining) to stay within
 * EmailJS free tier limits. Passes a short follow-up message as body_content.
 *
 * Returns { success: true } or { success: false, error: ... }
 */
export const sendQuickAddFollowUp = async ({ contactName, contactEmail }) => {
	const bodyContent = `<!-- Quick Add Follow-Up -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 24px; margin-bottom: 16px; border-left: 4px solid #d15a36;">
<h1 style="color: #d15a36; font-size: 22px; margin: 0 0 16px 0;">Hey ${contactName}!</h1>
<p style="margin: 0 0 12px 0; line-height: 1.6; color: #333333;">Great meeting you! We'd love to help you hit your health and fitness goals.</p>
<p style="margin: 0 0 12px 0; line-height: 1.6; color: #333333;">Urban 59 is a small group personal training studio in Sidcup — specialising in strength training with coaches who actually know your name.</p>
<p style="margin: 0 0 16px 0; line-height: 1.6; color: #333333;">When you're ready to get started or if you want to book a free consultation, please visit:</p>
<div style="text-align: center; margin-bottom: 16px;">
<a href="${APP_URL}" target="_blank" style="display: inline-block; background-color: #d15a36; color: #ffffff; text-decoration: none; font-weight: bold; padding: 12px 24px; border-radius: 6px; font-size: 15px;">Visit Urban59.co.uk</a>
</div>
<p style="margin: 0; line-height: 1.6; color: #333333;">Any questions? Just reply to this email or WhatsApp us on <span class="x-apple-no-detect" style="color: #333333; text-decoration: none;">07575\u200B 391059</span>.</p>
</div>`;

	try {
		await emailjs.send(
			EMAILJS_SERVICE_ID,
			EMAILJS_JOINING_TEMPLATE_ID,
			{
				to_name: contactName,
				to_email: contactEmail,
				body_content: bodyContent,
				email_subject: `Great meeting you, ${contactName}!`,
			},
			EMAILJS_PUBLIC_KEY,
		);
		console.log("Quick Add follow-up email sent to:", contactEmail);
		return { success: true };
	} catch (error) {
		console.error("Failed to send Quick Add follow-up email:", error);
		return {
			success: false,
			error: error?.text || error?.message || "Unknown error",
		};
	}
};

/**
 * Sends a "register interest" notification when someone signs up to hear about
 * a coming-soon location (e.g. Ebbsfleet).
 *
 * Reuses the shared joining template (template_joining) to stay within the
 * EmailJS free-tier 2-template limit — same pattern as sendQuickAddFollowUp.
 * The email is sent to the Urban 59 notification inbox and clearly references
 * which location the interest is for.
 *
 * Params passed to the template (branch in EmailJS on recipient_type if needed):
 *   to_name          - fixed label for the notification recipient
 *   to_email         - the notification inbox (NOT the lead's address)
 *   recipient_type   - 'register-interest' (lets the template branch if desired)
 *   location_id      - machine id of the location, e.g. 'ebbsfleet'
 *   location_name    - display name, e.g. 'Urban 59 Ebbsfleet'
 *   body_content     - rendered HTML summary of the lead's details
 *   email_subject    - subject line referencing the location
 *
 * Fails silently (logs only) so it never blocks the Firestore write.
 * Returns { success: true } or { success: false, error }.
 */
export const sendRegisterInterestNotification = async ({
	firstName,
	lastName,
	email,
	phone,
	message,
	locationId,
	locationName,
}) => {
	const fullName = `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
	const notificationInbox = NOTIFICATION_EMAIL;

	const bodyContent = `<!-- Register Interest -->
<div style="background-color: #ffffff; border-radius: 8px; padding: 24px; margin-bottom: 16px; border-left: 4px solid #d15a36;">
<h1 style="color: #d15a36; font-size: 22px; margin: 0 0 16px 0;">New interest: ${locationName || "Urban 59"}</h1>
<p style="margin: 0 0 12px 0; line-height: 1.6; color: #333333;">Someone registered their interest in <strong>${locationName || "a new Urban 59 location"}</strong>.</p>
<table style="width: 100%; border-collapse: collapse;">
<tr><td style="padding: 6px 0; color: #888888;">Name</td><td style="padding: 6px 0; color: #333333;"><strong>${fullName}</strong></td></tr>
<tr><td style="padding: 6px 0; color: #888888;">Email</td><td style="padding: 6px 0; color: #333333;">${email || "Not provided"}</td></tr>
<tr><td style="padding: 6px 0; color: #888888;">Phone</td><td style="padding: 6px 0; color: #333333;">${phone || "Not provided"}</td></tr>
<tr><td style="padding: 6px 0; color: #888888;">Location</td><td style="padding: 6px 0; color: #333333;">${locationName || ""} (${locationId || ""})</td></tr>
</table>
${
	message
		? `<p style="margin: 16px 0 0 0; line-height: 1.6; color: #333333;"><strong>Message:</strong><br/>${message}</p>`
		: ""
}
</div>`;

	try {
		await emailjs.send(
			EMAILJS_SERVICE_ID,
			EMAILJS_JOINING_TEMPLATE_ID,
			{
				to_name: "Urban 59 Team",
				to_email: notificationInbox,
				recipient_type: "register-interest",
				location_id: locationId || "",
				location_name: locationName || "",
				body_content: bodyContent,
				email_subject: `New ${locationName || "Urban 59"} interest: ${fullName}`,
			},
			EMAILJS_PUBLIC_KEY,
		);
		console.log("Register-interest notification sent for:", locationId);
		return { success: true };
	} catch (error) {
		console.error("Failed to send register-interest notification:", error);
		return {
			success: false,
			error: error?.text || error?.message || "Unknown error",
		};
	}
};
